// src/components/appSettings/Collection/CollectionForm/CollectionForm.jsx
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCollectionByIdThunk } from "../../../../store/thunks/appSettingsThunks";
import { useCollectionForm } from "./collectionFormHooks";
import Button from "../../../../components/common/Button";
import toast from "react-hot-toast";
import {
  CollectionBasicInfoSection,
  CollectionMediaSection,
  CollectionFiltersSection,
  CollectionDisplaySection,
  CollectionMetadataSection,
  CollectionTargetingSection,
  CollectionSEOSection,
} from "./CollectionFormSections";

const CollectionForm = ({ mode = "add" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedCollection, loading } = useSelector(
    (state) => state.appSettings
  );
  const { branches } = useSelector((state) => state.branch);
  const { categories } = useSelector((state) => state.category);

  // Use custom form hook
  const {
    formData,
    errors,
    submitting,
    imageFiles,
    imagePreviews,
    handleChange,
    handleArrayChange,
    handleImageUpload,
    handleImageRemove,
    handleSubmit,
    validateForm,
  } = useCollectionForm(selectedCollection, mode);

  // Load collection data for edit mode
  useEffect(() => {
    if (mode === "edit" && id) {
      dispatch(fetchCollectionByIdThunk(id));
    }
  }, [mode, id, dispatch]);

  // Handle form submission
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    await handleSubmit();
  };

  if (mode === "edit" && loading && !selectedCollection) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6 pb-20">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === "add" ? "Create New Collection" : "Edit Collection"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {mode === "add"
                ? "Create a new product collection"
                : `Editing: ${selectedCollection?.name || "Collection"}`}
            </p>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <CollectionBasicInfoSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Media Upload */}
      <CollectionMediaSection
        formData={formData}
        imageFiles={imageFiles}
        imagePreviews={imagePreviews}
        errors={errors}
        onImageUpload={handleImageUpload}
        onImageRemove={handleImageRemove}
        mode={mode}
      />

      {/* Product Filters */}
      <CollectionFiltersSection
        formData={formData}
        errors={errors}
        categories={categories}
        onChange={handleChange}
        onArrayChange={handleArrayChange}
      />

      {/* Display Settings */}
      <CollectionDisplaySection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Metadata */}
      <CollectionMetadataSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Targeting */}
      <CollectionTargetingSection
        formData={formData}
        errors={errors}
        branches={branches}
        onChange={handleChange}
        onArrayChange={handleArrayChange}
      />

      {/* SEO */}
      <CollectionSEOSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
        onArrayChange={handleArrayChange}
      />

      {/* Form Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-10">
        <div className="container mx-auto flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/app-settings/collections")}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={submitting} disabled={submitting}>
            {submitting
              ? "Saving..."
              : mode === "add"
              ? "Create Collection"
              : "Update Collection"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CollectionForm;
